import React, { useEffect, useState } from "react";
import { Card, Tag, Row, Col, Spin } from "antd";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { getData } from "../../../../../../actions/apiCall";
import { useDispatch, useSelector } from "react-redux";
import dayjs, { Dayjs } from "dayjs";

interface OverviewConfig {
  title: string;
  targetLabel: string;
  status: {
    label: string;
    color: string;
    bg: string;
  };
  barColor: string;
  activeBarColor: string;
  tooltipLabel: string;
  summaryLabel: string;
  weeklyData: Array<{ day: string; value: number }>;
  target: number;
  achieved: number;
  percentage: number;
  bestDay: string;
  worstDay: string;
  average: number;
}

interface OverviewDashboardProps {
  bdeId?: string | null;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  period?: string;
}

const defaultWeeklyData = [
  { day: "Sun", value: 6 },
  { day: "Mon", value: 10 },
  { day: "Tue", value: 6 },
  { day: "Wed", value: 12 },
  { day: "Thu", value: 5 },
  { day: "Fri", value: 9 },
  { day: "Sat", value: 4 },
];

const defaultLeadsConfig: OverviewConfig = {
  title: "Leads Overview",
  targetLabel: "Target: 30 Leads/Month",
  status: { label: "On Track", color: "text-green-600", bg: "bg-green-100" },
  barColor: "#c7c6ff",
  activeBarColor: "#6c63ff",
  tooltipLabel: "leads added",
  summaryLabel: "Leads",
  weeklyData: defaultWeeklyData,
  target: 30,
  achieved: 12,
  percentage: 40,
  bestDay: "Wed",
  worstDay: "Thu",
  average: 7.4
};

const defaultDealsConfig: OverviewConfig = {
  title: "Deals Overview",
  targetLabel: "Target: 30 Deals/Month",
  status: {
    label: "Needs Attention",
    color: "text-red-600",
    bg: "bg-red-100",
  },
  barColor: "#ffd7b0",
  activeBarColor: "#ff8a1f",
  tooltipLabel: "Deals created",
  summaryLabel: "Deals",
  weeklyData: defaultWeeklyData,
  target: 30,
  achieved: 12,
  percentage: 40,
  bestDay: "Wed",
  worstDay: "Thu",
  average: 7.4
};

const OverviewCard: React.FC<{ config: OverviewConfig }> = ({ config }) => {
  // Find the day with highest value for display
  const bestDayData = config.weeklyData.reduce((best, day) =>
    day.value > best.value ? day : best,
    config.weeklyData[0] || { day: 'N/A', value: 0 }
  );

  return (
    <Card
      title={<span className="font-semibold">{config.title}</span>}
      extra={
        <span
          className={`text-xs px-3 py-1 rounded-full ${config.status.bg} ${config.status.color}`}
        >
          {config.status.label}
        </span>
      }
      className="rounded-xl border border-gray-100"
      bodyStyle={{ padding: 16 }}
    >
      {/* Target */}
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm text-gray-600">{config.targetLabel}</span>
      </div>

      {/* Chart */}
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={config.weeklyData} barSize={8}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: "transparent" }}
              content={({ payload, label }) =>
                payload?.length ? (
                  <div className="bg-white px-3 py-2 rounded-lg shadow text-xs">
                    <p className="text-gray-400">{label}</p>
                    <p className="font-medium text-indigo-600">
                      {payload[0].value} {config.tooltipLabel}
                    </p>
                  </div>
                ) : null
              }
            />
            <Bar
              dataKey="value"
              radius={[6, 6, 6, 6]}
              fill={config.barColor}
              activeBar={{ fill: config.activeBarColor }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div >

      {/* Stats */}
      < div className="grid grid-cols-3 gap-3 mt-4" >
        <div className="border rounded-lg p-3">
          <p className="text-sm font-medium text-indigo-600">{config.bestDay}</p>
          <p className="text-xs text-gray-500">
            {bestDayData.value} {config.summaryLabel} added
          </p>
        </div>

        <div className="border rounded-lg p-3">
          <p className="text-xs text-gray-500">Target: {config.target}</p>
          <p className="text-sm font-semibold">
            Achieved: <span className="text-indigo-600">{config.achieved}</span>
          </p>
        </div>

        <div className="border rounded-lg p-3 text-center">
          <p className="text-xl font-semibold">{config.percentage}%</p>
          <p className="text-xs text-gray-500">of target</p>
        </div >
      </div >

      {/* Trend Summary */}
      < div className="border rounded-lg p-3 mt-4" >
        <p className="text-sm font-medium mb-2">Trend Summary</p>
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <span>🟢 Best: {config.bestDay}</span>
          <span>🟠 Average: {config.average.toFixed(1)}/{config.summaryLabel}</span>
          <span>🔴 Low: {config.worstDay}</span>
        </div >
      </div >
    </Card >
  );
};


const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  bdeId,
  startDate,
  endDate,
  period = 'month'
}) => {
  const [trackerData, setTrackerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { user, isGlobalAdmin } = useSelector((state: any) => state.app);
  const isAdmin = isGlobalAdmin || user?.designation === 'Admin';

  useEffect(() => {
    if (startDate && endDate && (isAdmin || bdeId)) {
      fetchTrackerData();
    }
  }, [bdeId, startDate, endDate, period, isAdmin]);

  const fetchTrackerData = async () => {
    if (!startDate || !endDate) return;
    if (!isAdmin && !bdeId) return;

    try {
      setLoading(true);
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const bdeIdParam = bdeId === 'all' || !bdeId ? 'all' : bdeId;

      const res = await dispatch(
        getData(`/targets/bde/tracker-dashboard?bdeId=${bdeIdParam}&startDate=${startDateStr}&endDate=${endDateStr}&period=${period}`)
      );

      if (res?.code === 200 && res?.data) {
        setTrackerData(res.data);
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build configs from API data
  const leadsConfig: OverviewConfig = trackerData?.leadsOverview ? {
    title: trackerData.leadsOverview.title,
    targetLabel: trackerData.leadsOverview.targetLabel,
    status: trackerData.leadsOverview.status,
    barColor: "#c7c6ff",
    activeBarColor: "#6c63ff",
    tooltipLabel: "leads added",
    summaryLabel: "Leads",
    weeklyData: trackerData.leadsOverview.weeklyData,
    target: trackerData.leadsOverview.target,
    achieved: trackerData.leadsOverview.achieved,
    percentage: trackerData.leadsOverview.percentage,
    bestDay: trackerData.leadsOverview.bestDay,
    worstDay: trackerData.leadsOverview.worstDay,
    average: trackerData.leadsOverview.average
  } : defaultLeadsConfig;

  const dealsConfig: OverviewConfig = trackerData?.dealsOverview ? {
    title: trackerData.dealsOverview.title,
    targetLabel: trackerData.dealsOverview.targetLabel,
    status: trackerData.dealsOverview.status,
    barColor: "#ffd7b0",
    activeBarColor: "#ff8a1f",
    tooltipLabel: "Deals created",
    summaryLabel: "Deals",
    weeklyData: trackerData.dealsOverview.weeklyData,
    target: trackerData.dealsOverview.target,
    achieved: trackerData.dealsOverview.achieved,
    percentage: trackerData.dealsOverview.percentage,
    bestDay: trackerData.dealsOverview.bestDay,
    worstDay: trackerData.dealsOverview.worstDay,
    average: trackerData.dealsOverview.average
  } : defaultDealsConfig;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      {loading ? (
        <div className="col-span-2 flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <OverviewCard config={leadsConfig} />
          <OverviewCard config={dealsConfig} />
        </>
      )}
    </div>
  );
};

export default OverviewDashboard;
